import { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import type { D3ScoreboardParams, D3ScoreboardState } from '../types';

interface Props {
  params: D3ScoreboardParams;
  puzzleState: D3ScoreboardState;
  onSolve: () => void;
  patchState: (updater: (prev: D3ScoreboardState) => D3ScoreboardState) => void;
}

const W = 360;
const H = 220;
const MARGIN = { top: 20, right: 20, bottom: 40, left: 40 };
const INNER_W = W - MARGIN.left - MARGIN.right;
const INNER_H = H - MARGIN.top - MARGIN.bottom;

export function D3Scoreboard({ params, puzzleState, onSolve, patchState }: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const valuesRef = useRef(puzzleState.values);
  const dragCountRef = useRef(puzzleState.dragCount);
  const solvedRef = useRef(puzzleState.solved);
  const patchRef = useRef(patchState);
  const onSolveRef = useRef(onSolve);

  // Keep refs in sync with latest props
  useEffect(() => { valuesRef.current = puzzleState.values; }, [puzzleState.values]);
  useEffect(() => { dragCountRef.current = puzzleState.dragCount; }, [puzzleState.dragCount]);
  useEffect(() => { solvedRef.current = puzzleState.solved; }, [puzzleState.solved]);
  useEffect(() => { patchRef.current = patchState; }, [patchState]);
  useEffect(() => { onSolveRef.current = onSolve; }, [onSolve]);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const root = svg
      .attr('viewBox', `0 0 ${W} ${H}`)
      .append('g')
      .attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

    const metrics = params.metrics;
    const ids = metrics.map(m => m.id);

    const x = d3.scaleBand().domain(ids).range([0, INNER_W]).padding(0.25);
    const y = d3.scaleLinear().domain([0, 100]).range([INNER_H, 0]);

    // Axes
    root.append('g')
      .attr('transform', `translate(0,${INNER_H})`)
      .call(d3.axisBottom(x).tickFormat(id => {
        const m = metrics.find(m => m.id === id);
        return m?.label.slice(0, 5) ?? id;
      }))
      .call(g => g.select('.domain').attr('stroke', 'var(--color-border)'))
      .call(g => g.selectAll('text').attr('fill', 'var(--color-fg)').style('font-size', '10px'));

    root.append('g')
      .call(d3.axisLeft(y).ticks(5))
      .call(g => g.select('.domain').attr('stroke', 'var(--color-border)'))
      .call(g => g.selectAll('text').attr('fill', 'var(--color-fg)').style('font-size', '10px'));

    // Bars
    const barGroup = root.append('g').attr('class', 'bars');

    function renderBars(values: Record<string, number>) {
      barGroup.selectAll<SVGRectElement, typeof metrics[0]>('rect.bar')
        .data(metrics, d => d.id)
        .join('rect')
        .attr('class', 'bar')
        .attr('x', d => x(d.id) ?? 0)
        .attr('y', d => y(values[d.id] ?? d.initialValue))
        .attr('width', x.bandwidth())
        .attr('height', d => INNER_H - y(values[d.id] ?? d.initialValue))
        .attr('fill', 'var(--color-primary, #2493A2)')
        .attr('rx', 3);

      // Value labels
      barGroup.selectAll<SVGTextElement, typeof metrics[0]>('text.val')
        .data(metrics, d => d.id)
        .join('text')
        .attr('class', 'val')
        .attr('x', d => (x(d.id) ?? 0) + x.bandwidth() / 2)
        .attr('y', d => y(values[d.id] ?? d.initialValue) - 4)
        .attr('text-anchor', 'middle')
        .attr('fill', 'var(--color-fg)')
        .style('font-size', '10px')
        .text(d => Math.round(values[d.id] ?? d.initialValue));

      // Drag handles (circles on top of bars)
      const handles = barGroup.selectAll<SVGCircleElement, typeof metrics[0]>('circle.handle')
        .data(metrics, d => d.id)
        .join('circle')
        .attr('class', 'handle')
        .attr('cx', d => (x(d.id) ?? 0) + x.bandwidth() / 2)
        .attr('cy', d => y(values[d.id] ?? d.initialValue))
        .attr('r', 7)
        .attr('fill', 'var(--color-fg)')
        .attr('stroke', 'var(--color-primary, #2493A2)')
        .attr('stroke-width', 2)
        .style('cursor', 'ns-resize');

      const drag = d3.drag<SVGCircleElement, typeof metrics[0]>()
        .on('drag', function(event, d) {
          if (solvedRef.current) return;
          let newVal = Math.max(0, Math.min(100, Math.round(y.invert(event.y))));
          const current = valuesRef.current;
          const delta = newVal - (current[d.id] ?? d.initialValue);

          const nextValues = { ...current, [d.id]: newVal };

          // Apply linked tradeoff
          if (d.linkedTo && d.linkFactor !== undefined) {
            const linked = metrics.find(m => m.id === d.linkedTo);
            if (linked) {
              const linkedNew = Math.max(0, Math.min(100,
                (current[linked.id] ?? linked.initialValue) - delta * d.linkFactor
              ));
              nextValues[linked.id] = Math.round(linkedNew);
            }
          }

          valuesRef.current = nextValues;
          renderBars(nextValues);
        })
        .on('end', function(_) {
          if (solvedRef.current) return;
          const values = valuesRef.current;
          const newCount = dragCountRef.current + 1;
          dragCountRef.current = newCount;
          patchRef.current(s => ({ ...s, values, dragCount: newCount }));
        });

      handles.call(drag as d3.DragBehavior<SVGCircleElement, typeof metrics[0], unknown>);
    }

    renderBars(valuesRef.current);

    return () => { svg.selectAll('*').remove(); };
  }, [params.metrics]); // eslint-disable-line react-hooks/exhaustive-deps

  const canAccept = puzzleState.dragCount >= params.requiredDrags && !puzzleState.solved;

  function handleAccept() {
    patchState(s => ({ ...s, solved: true }));
    onSolve();
  }

  if (puzzleState.solved) {
    return (
      <p className="puzzle-solved-msg">
        <i className="fa-solid fa-circle-check" aria-hidden="true" /> Scoreboard accepted.
      </p>
    );
  }

  return (
    <div className="puzzle-scoreboard">
      <p className="puzzle-narrative">
        Drag the bar handles to optimize your scores. Watch what happens to linked metrics.
      </p>
      <svg ref={svgRef} className="puzzle-scoreboard__svg" />
      <p className="puzzle-scoreboard__drag-count">
        Drags: {puzzleState.dragCount} / {params.requiredDrags} required
      </p>
      {canAccept && (
        <button
          className="escape-btn escape-btn--primary escape-btn--sm"
          type="button"
          onClick={handleAccept}
        >
          <i className="fa-solid fa-check" aria-hidden="true" /> Accept Scoreboard
        </button>
      )}
      {!canAccept && (
        <p className="puzzle-scoreboard__hint">
          Drag {params.requiredDrags - puzzleState.dragCount} more bar{params.requiredDrags - puzzleState.dragCount !== 1 ? 's' : ''} to continue.
        </p>
      )}
    </div>
  );
}
